import { useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { PatientChartSummaryData } from "src/api/nurse/nurseServiceDtos";
import InlineDateSelect from "./inline-date-select";

type GeneralInfoProps = {
  summary: PatientChartSummaryData;
  loading?: boolean;
};

type InfoItem = {
  label: string;
  value: string;
};

const formatMetric = (value: number | null | undefined) =>
  value === null || value === undefined ? "-" : String(value);

export default function GeneralInfo({ summary, loading = false }: Readonly<GeneralInfoProps>) {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const items: InfoItem[] = useMemo(
    () => [
      { label: "Yatış Günü", value: loading ? "..." : formatMetric(summary.lengthofstaydays) },
      { label: "Aktif İlaç Sayısı", value: loading ? "..." : formatMetric(summary.activemedicationcount) },
      { label: "Panik Değerler", value: loading ? "..." : formatMetric(summary.panicvaluecount) },
      { label: "Alerjiler", value: loading ? "..." : formatMetric(summary.allergycount) },
    ],
    [loading, summary]
  );

  return (
    <Box
      sx={{
        width: "100%",
        px: { xs: "12px", sm: "28px" },
        py: { xs: "12px", sm: "10px" },
        background: "#fff",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: { xs: "16px", sm: "24px" },
      }}
    >
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          gap: 1.5,
        }}
      >
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <InlineDateSelect value={selectedDate} onChange={setSelectedDate} />
        </Box>

        <Box
          sx={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            borderTop: "1px solid #E5EAF0",
            borderLeft: "1px solid #E5EAF0",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          {items.map((item) => (
            <Box
              key={item.label}
              sx={{
                minWidth: 0,
                px: "10px",
                py: "10px",
                borderRight: "1px solid #E5EAF0",
                borderBottom: "1px solid #E5EAF0",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "center",
                gap: "2px",
                background: "#fff",
              }}
            >
              <Typography
                sx={{
                  color: "#000",
                  fontSize: "14px",
                  fontFamily: "Poppins",
                  fontWeight: 400,
                  lineHeight: "18px",
                }}
              >
                {item.label}
              </Typography>
              <Typography
                sx={{
                  color: "#2979C3",
                  fontSize: "28px",
                  fontFamily: "Poppins",
                  fontWeight: 400,
                  lineHeight: "32px",
                }}
              >
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        sx={{ width: "100%", overflowX: "hidden", display: { xs: "none", sm: "flex" } }}
      >
        <InlineDateSelect value={selectedDate} onChange={setSelectedDate} />

        {items.map((item, index) => (
          <Box
            key={item.label}
            sx={{
              height: "30px",
              px: "10px",
              py: "5px",
              ml: index === 0 ? "12px" : 0,
              borderLeft: index === 0 ? "none" : "1px solid #E5EAF0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
              <Typography
                sx={{
                  color: "#000",
                  fontSize: "20px",
                  fontFamily: "Poppins",
                  fontWeight: 400,
                  lineHeight: "24px",
                  wordWrap: "break-word",
                }}
              >
                {item.label}
              </Typography>
              <Typography
                sx={{
                  color: "#616877",
                  fontSize: "20px",
                  fontFamily: "Poppins",
                  fontWeight: 400,
                  wordWrap: "break-word",
                }}
              >
                {item.value}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
